import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ReportData, ReportSummary } from "../../domain";
import { fmt, fmtDate, fmtPct, getIndicator } from "./report/helpers";
import { ReportSkeleton } from "./report/ReportSkeleton";
import { AudienceSection } from "./report/AudienceSection";
import { MediaRow } from "./report/MediaRow";
import { apiClient } from "../../data";
import { mockReport } from "../../data/mockReport";
import { analytics } from "../../analytics";
import "./ReportPage.css";

const USE_MOCK = process.env.REACT_APP_USE_MOCK_REPORT === "true";


// ── Icons ─────────────────────────────────────────────────────────────────────

const IgIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="3" width="18" height="18" rx="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none"/>
  </svg>
);

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="23 4 23 10 17 10"/>
    <path d="M20.5 16.5A9 9 0 1 1 21 10"/>
  </svg>
);

const GlobeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);

const GearIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="3"/>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
  </svg>
);

const ErrIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 8v5"/>
    <circle cx="12" cy="16.5" r="0.6" fill="currentColor" stroke="none"/>
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="8" r="3.5"/>
    <path d="M3 21a9 9 0 0 1 18 0"/>
    <path d="M12 14v7M9 17h6"/>
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name?: string | null, username?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (username || "??").slice(0, 2).toUpperCase();
}

function dotClass(level: string): string {
  if (level === "green") return "green";
  if (level === "yellow") return "yellow";
  return "red";
}

// ── SummaryCard ───────────────────────────────────────────────────────────────

interface SummaryCardProps { label: string; value: string }
const SummaryCard: React.FC<SummaryCardProps> = ({ label, value }) => (
  <div className="sum-card">
    <div className="sum-val">{value}</div>
    <div className="sum-lbl">{label}</div>
  </div>
);

// ── EngagementCard ────────────────────────────────────────────────────────────

interface EngCardProps { value: string; name: string; hint: string; tooltip: string; level: string }
const EngCard: React.FC<EngCardProps> = ({ value, name, hint, tooltip, level }) => (
  <div className="eng-card" tabIndex={0}>
    <div className="eng-val">
      <span className={`tl-dot ${dotClass(level)}`} />
      {value}
    </div>
    <div className="eng-name">{name}</div>
    <div className="eng-hint">{hint}</div>
    <div className="eng-tooltip">{tooltip}</div>
  </div>
);

// ── Main page ─────────────────────────────────────────────────────────────────

export const ReportPage: React.FC<{ isPublic?: boolean }> = ({ isPublic = false }) => {
  const navigate = useNavigate();
  const { username } = useParams<{ username: string }>();
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en-US" : "ru-RU";

  const [report, setReport] = React.useState<ReportData | null>(() => {
    if (USE_MOCK) return mockReport;
    if (isPublic) return null;
    const raw = localStorage.getItem("report_data");
    if (raw) { try { return JSON.parse(raw); } catch {} }
    return null;
  });

  const [catalogVisible, setCatalogVisible] = React.useState(true);
  const [catalogMessage, setCatalogMessage] = React.useState(USE_MOCK ? mockReport.catalog_message ?? "" : "");
  const [catalogSavedMessage, setCatalogSavedMessage] = React.useState(USE_MOCK ? mockReport.catalog_message ?? "" : "");
  const [catalogSaving, setCatalogSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(() => {
    if (USE_MOCK) return false;
    return isPublic || !localStorage.getItem("report_data");
  });
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const reportRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (USE_MOCK) return;
    if (isPublic) {
      if (!username) { setLoading(false); return; }
      let cancelled = false;
      (async () => {
        try {
          const reportData = await apiClient.getPublicReport(username);
          if (cancelled) return;
          setReport(reportData);
          analytics.publicReportViewed(username!);
        } catch (err: any) {
          if (cancelled) return;
          setFetchError(err.response?.data?.detail || t("report.errorTitle"));
        } finally {
          if (!cancelled) setTimeout(() => !cancelled && setLoading(false), 300);
        }
      })();
      return () => { cancelled = true; };
    }
    const token = localStorage.getItem("access_token");
    if (!token) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const startTime = Date.now();
      try {
        if (!report) {
          try {
            const reportData = await apiClient.generateReport(100);
            if (cancelled) return;
            localStorage.setItem("report_data", JSON.stringify(reportData));
            setReport(reportData);
            const elapsed = Date.now() - startTime;
            setTimeout(() => !cancelled && setLoading(false), Math.max(0, 300 - elapsed));
          } catch (err: any) {
            if (cancelled) return;
            setFetchError(err.response?.data?.detail || t("report.errorTitle"));
            setLoading(false);
            return;
          }
        } else {
          setLoading(false);
        }
        try {
          const creatorData = await apiClient.getCreator();
          if (cancelled) return;
          setCatalogVisible(creatorData.show_in_catalog);
          setCatalogMessage(creatorData.catalog_message || "");
          setCatalogSavedMessage(creatorData.catalog_message || "");
        } catch (err) {
          console.warn("Failed to load catalog settings", err);
        }
      } catch (err: any) {
        if (cancelled) return;
        setFetchError(err.response?.data?.detail || t("report.errorTitle"));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    if (!report) return;
    try {
      setRefreshing(true);
      const reportData = await apiClient.refreshReport(report.limit ?? 100);
      localStorage.setItem("report_data", JSON.stringify(reportData));
      setReport(reportData);
      analytics.reportRefreshed();
    } catch (err) {
      console.error("Failed to refresh report:", err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCatalogToggle = async () => {
    const next = !catalogVisible;
    setCatalogVisible(next);
    try {
      await apiClient.updateCreatorSettings({ show_in_catalog: next });
    } catch {
      setCatalogVisible(!next);
    }
  };

  const handleCatalogSave = async () => {
    setCatalogSaving(true);
    try {
      await apiClient.updateCreatorSettings({ catalog_message: catalogMessage });
      setCatalogSavedMessage(catalogMessage);
    } finally {
      setCatalogSaving(false);
    }
  };

  const exportPdf = async () => {
    if (!reportRef.current || !report) return;
    setPdfLoading(true);
    try {
      const el = reportRef.current;
      const tableWraps = Array.from(el.querySelectorAll<HTMLElement>(".posts-scroll"));
      const prevStyles = tableWraps.map(w => ({ overflow: w.style.overflow, width: w.style.width }));
      tableWraps.forEach(w => { w.style.overflow = "visible"; w.style.width = w.scrollWidth + "px"; });
      const naturalW = el.scrollWidth;
      const canvas = await html2canvas(el, {
        scale: 1.5, useCORS: true, allowTaint: false, backgroundColor: "#ffffff",
        imageTimeout: 8000, width: naturalW, windowWidth: naturalW,
        ignoreElements: node => node.classList.contains("no-print"),
      });
      tableWraps.forEach((w, i) => { w.style.overflow = prevStyles[i].overflow; w.style.width = prevStyles[i].width; });
      const imgData = canvas.toDataURL("image/jpeg", 0.82);
      const margin = 10, pageW = 210, contentW = pageW - margin * 2;
      const contentH = (canvas.height * contentW) / canvas.width;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [pageW, contentH + margin * 2] });
      pdf.addImage(imgData, "JPEG", margin, margin, contentW, contentH);
      const date = new Date(report.generated_at).toISOString().slice(0, 10);
      pdf.save(`report_${report.creator_username}_${date}.pdf`);
      analytics.reportPdfDownloaded();
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) return <ReportSkeleton />;

  // ── Error ────────────────────────────────────────────────────────────────────

  if (fetchError) {
    return (
      <div className="report-page">
        <div className="rp-sec">
          <div className="rp-state">
            <div className="rp-state-icon red"><ErrIcon /></div>
            <div className="rp-state-title">{t("report.errorTitle")}</div>
            <p className="rp-state-sub">{fetchError}</p>
            <div className="rp-state-actions">
              <button className="btn-secondary" onClick={() => { setFetchError(null); setLoading(true); window.location.reload(); }}>
                {t("report.retryButton")}
              </button>
              <button className="btn-secondary" onClick={() => {
                // Корень показывает отчёт, пока есть токен — для перелогина чистим его
                localStorage.removeItem("access_token");
                localStorage.removeItem("auth_method");
                navigate("/");
              }}>
                {t("report.reloginButton")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Empty ────────────────────────────────────────────────────────────────────

  if (!report) {
    return (
      <div className="report-page">
        <div className="rp-sec">
          <div className="rp-state">
            <div className="rp-state-icon gold"><UserIcon /></div>
            <div className="rp-state-title">{t("report.emptyReport")}</div>
            <p className="rp-state-sub">Подключи Instagram, чтобы получить аналитику и попасть в каталог рекламодателей.</p>
            <div className="rp-state-actions">
              <button className="btn-secondary" onClick={() => {
                localStorage.removeItem("access_token");
                localStorage.removeItem("auth_method");
                navigate("/");
              }}>
                {t("report.loginButton")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const s: ReportSummary = report.summary;
  const totalPosts = s.total_reels ?? s.total_posts ?? report.metrics.length;
  const hasEngagement = s.er != null || s.err != null || s.like_rate != null || s.comment_rate != null;
  const initials = getInitials(report.creator_name, report.creator_username);

  return (
    <div className="report-page" ref={reportRef}>

      {/* ── PROFILE ── */}
      <div className="rp-sec">
        <div className="rp-profile-inner">
          <div className="rp-av">
            {report.creator_profile_picture_url
              ? <img src={report.creator_profile_picture_url} alt={report.creator_username ? `Аватар @${report.creator_username}` : "Аватар профиля"} />
              : initials}
          </div>
          <div className="rp-info">
            {report.creator_name && <div className="rp-name">{report.creator_name}</div>}
            <a
              className="rp-username"
              href={`https://www.instagram.com/${report.creator_username}/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IgIcon /> @{report.creator_username}
            </a>
            <div className="rp-meta-row">
              {report.creator_followers_count != null && (
                <>
                  <span className="rp-meta-val">{fmt(report.creator_followers_count, locale)}</span>
                  <span className="rp-meta-lbl">подписчиков</span>
                  <span className="rp-meta-sep">·</span>
                </>
              )}
              <span className="rp-meta-period">
                {fmtDate(report.period_start, locale)} — {fmtDate(report.period_end, locale)}
              </span>
            </div>
            {report.creator_biography && <p className="rp-bio">{report.creator_biography}</p>}
            {report.creator_website && (
              <a
                className="rp-website"
                href={report.creator_website.startsWith("http") ? report.creator_website : `https://${report.creator_website}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GlobeIcon /> {report.creator_website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
          {!isPublic && (
            <div className="rp-actions no-print">
              <button className="rp-btn primary" onClick={exportPdf} disabled={pdfLoading || refreshing}>
                <PdfIcon /> {pdfLoading ? t("report.generatingPdf") : t("report.downloadPdf")}
              </button>
              <button className="rp-btn" onClick={handleRefresh} disabled={pdfLoading || refreshing}>
                <RefreshIcon /> {refreshing ? t("report.refreshing") : t("report.refresh")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── CATALOG MESSAGE (public) ── */}
      {isPublic && report.catalog_message && (
        <div className="rp-sec no-print">
          <div className="about-block">
            <div className="about-label">О блогере</div>
            <p className="about-text">{report.catalog_message}</p>
          </div>
        </div>
      )}

      {/* ── CATALOG SETTINGS (owner) ── */}
      {!isPublic && (
        <div className="rp-sec no-print">
          <div className="cat-panel">
            <div className="cat-panel-title"><GearIcon /> {t("report.catalog.toggleLabel")}</div>
            <div className="toggle-row">
              <label className="t-switch">
                <input
                  type="checkbox"
                  checked={catalogVisible}
                  onChange={handleCatalogToggle}
                />
                <div className="t-track" />
                <div className="t-thumb" />
              </label>
              <span className="t-label">{t("report.catalog.toggleLabel")}</span>
            </div>
            <p className="t-hint">{t("report.catalog.toggleHint")}</p>
            <div className="rp-textarea-wrap">
              <textarea
                className="rp-textarea"
                value={catalogMessage}
                onChange={e => setCatalogMessage(e.target.value.slice(0, 300))}
                rows={3}
                placeholder={t("report.catalog.messagePlaceholder")}
              />
              <span className="rp-textarea-counter">{catalogMessage.length}/300</span>
            </div>
            <div className="cat-panel-footer">
              <button
                className="rp-btn"
                onClick={handleCatalogSave}
                disabled={catalogSaving || catalogMessage === catalogSavedMessage}
              >
                {catalogSaving ? t("report.catalog.saving") : t("report.catalog.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SUMMARY ── */}
      <div className="rp-sec rp-sec-tinted">
        <div className="sec-header">
          <span className="sec-eyebrow">Аналитика</span>
          <h2 className="sec-title">
            {t("report.analyticsHeading", { count: totalPosts })}
            {report.creator_media_count != null && report.creator_media_count > totalPosts && (
              <span style={{ fontSize: "0.7em", fontWeight: 400, marginLeft: 8, color: "var(--ink-pale)" }}>
                {t("report.analyticsHeadingTotal", { total: fmt(report.creator_media_count, locale) })}
              </span>
            )}
          </h2>
        </div>
        <div className="summary-grid">
          {report.creator_followers_count != null && (
            <SummaryCard label={t("report.followers")} value={fmt(report.creator_followers_count, locale)} />
          )}
          <SummaryCard label={t("report.views")} value={fmt(s.total_plays, locale)} />
          <SummaryCard label={t("report.likes")} value={fmt(s.total_likes, locale)} />
          <SummaryCard label={t("report.comments")} value={fmt(s.total_comments, locale)} />
          <SummaryCard label={t("report.saves")} value={fmt(s.total_saves, locale)} />
          <SummaryCard label={t("report.shares")} value={fmt(s.total_shares, locale)} />
          {s.avg_plays_per_reel != null && (
            <SummaryCard
              label={t("report.avgViews")}
              value={Math.round(s.avg_plays_per_reel).toLocaleString(locale)}
            />
          )}
        </div>
      </div>

      {/* ── ENGAGEMENT ── */}
      {hasEngagement && (
        <div className="rp-sec">
          <div className="sec-header">
            <span className="sec-eyebrow">Вовлечённость</span>
            <h2 className="sec-title">{t("report.engagement")}</h2>
          </div>
          <div className="eng-grid">
            {s.er != null && (() => {
              const ind = getIndicator("er", s.er);
              return (
                <EngCard
                  value={fmtPct(s.er, locale)}
                  name="ER"
                  hint={t("report.erHint")}
                  tooltip={t(ind.tooltip)}
                  level={ind.level}
                />
              );
            })()}
            {s.err != null && (() => {
              const ind = getIndicator("err", s.err);
              return (
                <EngCard
                  value={fmtPct(s.err, locale)}
                  name="ERR"
                  hint={t("report.errHint")}
                  tooltip={t(ind.tooltip)}
                  level={ind.level}
                />
              );
            })()}
            {s.like_rate != null && (() => {
              const ind = getIndicator("like_rate", s.like_rate);
              return (
                <EngCard
                  value={fmtPct(s.like_rate, locale)}
                  name="Like Rate"
                  hint={t("report.likeRateHint")}
                  tooltip={t(ind.tooltip)}
                  level={ind.level}
                />
              );
            })()}
            {s.comment_rate != null && (() => {
              const ind = getIndicator("comment_rate", s.comment_rate);
              return (
                <EngCard
                  value={fmtPct(s.comment_rate, locale)}
                  name="Comment Rate"
                  hint={t("report.commentRateHint")}
                  tooltip={t(ind.tooltip)}
                  level={ind.level}
                />
              );
            })()}
          </div>
        </div>
      )}

      {/* ── AUDIENCE ── */}
      <AudienceSection audience={report.audience} />

      {/* ── POSTS ── */}
      <div className="rp-sec" style={{ borderBottom: "none" }}>
        <div className="sec-header">
          <span className="sec-eyebrow">Контент</span>
          <h2 className="sec-title">{t("report.posts", { count: totalPosts })}</h2>
        </div>
        {report.metrics.length === 0 ? (
          <p style={{ color: "var(--ink-pale)", margin: 0 }}>{t("report.noPosts")}</p>
        ) : (
          <>
            {/* desktop table */}
            <div className="posts-scroll">
              <table className="posts-table">
                <thead>
                  <tr>
                    <th>{t("report.tablePreview")}</th>
                    <th>{t("report.tableType")}</th>
                    <th>{t("report.tableDate")}</th>
                    <th>{t("report.tableCaption")}</th>
                    <th style={{ textAlign: "right" }}>{t("report.tableLikes")}</th>
                    <th style={{ textAlign: "right" }}>{t("report.tableComments")}</th>
                    <th style={{ textAlign: "right" }}>{t("report.tableSaves")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {report.metrics.map((item, i) => (
                    <MediaRow key={item.id} item={item} index={i} followersCount={report.creator_followers_count} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* mobile cards */}
            <div className="post-cards">
              {report.metrics.map((item) => {
                const isReel = item.media_type === "VIDEO";
                const isCarousel = item.media_type === "CAROUSEL_ALBUM";
                const typeLabel = isReel ? t("report.typeReel") : isCarousel ? t("report.typeCarousel") : t("report.typePhoto");
                const typeCls = isReel ? "type-badge reel" : isCarousel ? "type-badge carousel" : "type-badge photo";
                const viewsVal = item.views ?? item.plays ?? null;
                const hasReelMetrics = isReel && (viewsVal != null || item.reach != null || item.avg_watch_time_ms != null);
                const hasComm = (item.skus?.length ?? 0) > 0 || (item.brand_mentions?.length ?? 0) > 0;
                return (
                  <div key={item.id} className="post-card">
                    <div className="post-card-top">
                      <a href={item.permalink} target="_blank" rel="noopener noreferrer" className="post-card-thumb">
                        {item.cover_url
                          ? <img src={item.cover_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          : (isReel ? "▶" : isCarousel ? "◧" : "□")}
                      </a>
                      <div className="post-card-meta">
                        <div className="post-card-row1">
                          <span className={typeCls}>{typeLabel}</span>
                          <span className="post-card-date">{fmtDate(item.timestamp, locale)}</span>
                        </div>
                        {item.caption && (
                          <p className="post-card-caption">{item.caption.substring(0, 140)}</p>
                        )}
                      </div>
                    </div>
                    <div className="post-card-stats">
                      <div className="post-stat">
                        <span className="post-stat-val">{fmt(item.likes, locale)}</span>
                        <span className="post-stat-lbl">{t("report.tableLikes")}</span>
                      </div>
                      <div className="post-stat">
                        <span className="post-stat-val">{fmt(item.comments, locale)}</span>
                        <span className="post-stat-lbl">{t("report.tableComments")}</span>
                      </div>
                      <div className="post-stat">
                        <span className="post-stat-val">{fmt(item.saves, locale)}</span>
                        <span className="post-stat-lbl">{t("report.tableSaves")}</span>
                      </div>
                    </div>
                    {hasReelMetrics && (
                      <div className="post-card-reel">
                        {viewsVal != null && <span className="rchip"><span className="rc-dot green" />{fmt(viewsVal, locale)}&nbsp;просм.</span>}
                        {item.reach != null && <><span className="rchip-sep">·</span><span className="rchip">{fmt(item.reach, locale)}&nbsp;охват</span></>}
                        {item.avg_watch_time_ms != null && <><span className="rchip-sep">·</span><span className="rchip">{Math.round(item.avg_watch_time_ms / 1000)}с ср.просм.</span></>}
                      </div>
                    )}
                    {hasComm && (
                      <div className="post-card-comm">
                        <span className="comm-label">Коммерческий:</span>
                        {(item.skus ?? []).map((s, i) => <span key={i} className="sku-badge">{s}</span>)}
                        {(item.brand_mentions ?? []).map((b, i) => <span key={i} className="brand-badge">{b}</span>)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── FOOTER ── */}
      <footer className="report-footer">
        <span>{t("report.footerSource")}</span>
        <span>{t("report.footerGenerated", { date: fmtDate(report.generated_at, locale) })}</span>
      </footer>

    </div>
  );
};
